'use client';

import { applyCoupon } from '@eliadmin/core/src/api/cart/cart-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { Input } from '@eliadmin/core/src/components/ui/input';
import { useCart } from '@eliadmin/core/src/hooks/cart/use-cart';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React from 'react';

export function CartApplyCoupon() {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const [coupon, setCoupon] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const me = useCustomerStore(s => s.me);
  const queryClient = useQueryClient();

  const { data: cart } = useCart();
  const isAlreadyCouponApplied = !!cart?.coupons?.length;

  const applyCouponMutation = useMutation({
    meta: { affectsCart: true },
mutationFn: applyCoupon,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [CacheKey.CART] });
      setCoupon('');
    },
    onMutate: () => setError(null),
    onError: error => {
      if (error instanceof ValidationError && error.fields.coupon) {
        setError(error.fields.coupon.join(' '));
      } else {
        setError(t('text530'));
        if (process.env.NODE_ENV !== 'production') console.error(error);
      }
    },
  });

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
        <Input
          aria-label={t('text647')}
          placeholder={t('text3732')}
          value={coupon}
          onChange={e => setCoupon(e.target.value)}
          disabled={isAlreadyCouponApplied}
          className={{
            wrapper: cn('flex-1', error && 'border border-destructive'),
          }}
        />
        <Button
          onClick={() =>
            applyCouponMutation.mutate({ locale, body: { coupon }, authToken: me?.authToken })
          }
          variant={'secondary'}
          isLoading={applyCouponMutation.isPending}
          disabled={!coupon || isAlreadyCouponApplied}
          className="h-[52px] w-full whitespace-nowrap sm:w-fit"
        >
          {t('text645')}
        </Button>
      </div>
      {isAlreadyCouponApplied && <p className="text-xs text-success">{t('text545')}</p>}
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
